import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Download, Upload, ArrowLeft, CheckCircle2, AlertTriangle, XCircle, FileWarning, IdCard, Zap, ShieldAlert, Truck, Car, Cable, Eye, X, FileText } from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  carte_btp: IdCard,
  habilitation_electrique: Zap,
  autorisation_reseaux: ShieldAlert,
  autorisation_conduite: Truck,
  permis_conduite: Car,
  habilitation_fibre: Cable,
};
const STATUS_META = {
  green:   { cls: "border-emerald-200 bg-emerald-50", pill: "bg-emerald-100 text-emerald-700", label: "Valide", Icon: CheckCircle2 },
  orange:  { cls: "border-amber-200 bg-amber-50",     pill: "bg-amber-100 text-amber-700",     label: "Bientôt expirée", Icon: AlertTriangle },
  red:     { cls: "border-rose-300 bg-rose-50",       pill: "bg-rose-100 text-rose-700",       label: "Expire / Expirée", Icon: XCircle },
  missing: { cls: "border-slate-200 bg-slate-50",     pill: "bg-slate-100 text-slate-600",     label: "Non renseignée", Icon: FileWarning },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function TechDocs() {
  const { userId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [previewFor, setPreviewFor] = useState(null);
  const isRH = user?.role === "rh";
  const targetId = userId || user?.id;

  const load = async () => {
    try { const { data } = await api.get(`/tech-docs/user/${targetId}`); setData(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [targetId]);

  const download = async (d) => {
    try {
      const res = await api.get(`/tech-docs/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.filename || d.doc_type;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const docs = data?.documents || [];

  return (
    <div>
      <div className="mb-4">
        <Button variant="ghost" onClick={() => nav(-1)} data-testid="techdocs-back"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Button>
      </div>
      <PageHeader
        title={`Documents professionnels — ${data?.user_name || "…"}`}
        subtitle={isRH
          ? "Documents obligatoires pour l'exercice de l'activité de technicien. Attribuez les types requis depuis la fiche employé."
          : "Voici les documents attribués à votre profil. Cliquez sur un document pour l'aperçu ou le télécharger."}
      />

      {docs.length === 0 ? (
        <Card className="p-12 text-center border-slate-200 bg-slate-50/40">
          <FileWarning className="h-10 w-10 text-slate-300 mx-auto" />
          <div className="mt-3 text-sm text-slate-600">
            {isRH
              ? "Aucun document professionnel n'est attribué à cet employé. Rendez-vous dans « Employés → Modifier » pour cocher les documents applicables."
              : "Aucun document professionnel ne vous a été attribué. Contactez la RH si vous pensez qu'il s'agit d'une erreur."}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {docs.map((d) => {
            const meta = STATUS_META[d.status] || STATUS_META.missing;
            const Icon = ICONS[d.doc_type] || FileWarning;
            const StIcon = meta.Icon;
            return (
              <Card key={d.doc_type} data-testid={`techdoc-${d.doc_type}`} className={`p-5 border ${meta.cls}`}>
                <div className="flex items-start justify-between">
                  <div className={`h-10 w-10 rounded-lg bg-white flex items-center justify-center text-slate-700 shadow-sm`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${meta.pill}`}>
                    <StIcon className="h-3 w-3" /> {meta.label}
                  </span>
                </div>
                <div className="mt-4">
                  <div className="font-heading font-semibold text-slate-900 leading-tight">{d.label}</div>
                  <div className="text-xs text-slate-600 mt-1">
                    Validité : <b>{fmtDate(d.valid_until)}</b>
                    {d.days_left !== null && d.status !== "green" && (
                      <> · <span className={meta.pill.includes("emerald") ? "" : "font-medium"}>
                        {d.days_left < 0 ? `expirée depuis ${-d.days_left} j` : `dans ${d.days_left} j`}
                      </span></>
                    )}
                  </div>
                  {d.filename && <div className="text-[11px] text-slate-500 mt-1 truncate">{d.filename}</div>}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {d.id && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setPreviewFor(d)} data-testid={`pv-${d.doc_type}`} className="flex-1"><Eye className="h-3.5 w-3.5 mr-1" />Aperçu</Button>
                      <Button variant="outline" size="sm" onClick={() => download(d)} data-testid={`dl-${d.doc_type}`} className="flex-1"><Download className="h-3.5 w-3.5 mr-1" />Télécharger</Button>
                    </>
                  )}
                  {isRH && (
                    <Button size="sm" onClick={() => setUploadFor(d)} data-testid={`up-${d.doc_type}`} className="flex-1 bg-[#0066FF] hover:bg-[#0052CC]"><Upload className="h-3.5 w-3.5 mr-1" />{d.id ? "Renouveler" : "Déposer"}</Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {isRH && uploadFor && (
        <UploadDialog
          doc={uploadFor}
          userId={targetId}
          onClose={() => setUploadFor(null)}
          onDone={() => { setUploadFor(null); load(); }}
        />
      )}

      {previewFor && (
        <PreviewDialog doc={previewFor} onClose={() => setPreviewFor(null)} />
      )}
    </div>
  );
}

function PreviewDialog({ doc, onClose }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let currentUrl = null;
    setLoading(true); setError(null);
    api.get(`/tech-docs/${doc.id}/preview`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        currentUrl = URL.createObjectURL(res.data);
        setObjectUrl(currentUrl);
      })
      .catch((e) => { if (!cancelled) setError(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [doc.id]);

  const mime = (doc.content_type || "").toLowerCase();
  const isImage = mime.startsWith("image/");
  const isPdf = mime === "application/pdf" || (doc.filename || "").toLowerCase().endsWith(".pdf");

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl w-[95vw] h-[85vh] flex flex-col p-0 gap-0" data-testid="techdoc-preview-dialog">
        <DialogHeader className="p-5 pb-3 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2 text-base"><FileText className="h-4 w-4" /> {doc.label}</DialogTitle>
          <DialogDescription className="text-xs">
            {doc.filename || doc.doc_type} · Validité {fmtDate(doc.valid_until)}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 bg-slate-50">
          {loading && <div className="h-full flex items-center justify-center text-sm text-slate-500">Chargement de l'aperçu…</div>}
          {error && !loading && <div className="h-full flex items-center justify-center text-sm text-rose-600">{error}</div>}
          {!loading && !error && objectUrl && isImage && (
            <div className="h-full overflow-auto flex items-center justify-center p-4">
              <img src={objectUrl} alt={doc.label} className="max-w-full max-h-full object-contain shadow" data-testid="techdoc-preview-image" />
            </div>
          )}
          {!loading && !error && objectUrl && isPdf && (
            <iframe src={objectUrl} title={doc.label} className="w-full h-full border-0" data-testid="techdoc-preview-iframe" />
          )}
          {!loading && !error && objectUrl && !isImage && !isPdf && (
            <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center">
              <FileText className="h-10 w-10 text-slate-300" />
              <div className="text-sm text-slate-600">Aperçu indisponible pour ce format ({mime || "inconnu"}).</div>
              <a href={objectUrl} download={doc.filename || doc.doc_type} className="text-[#0066FF] text-sm underline">Télécharger le fichier</a>
            </div>
          )}
        </div>
        <div className="p-3 border-t border-slate-100 flex justify-end gap-2 bg-white">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="techdoc-preview-close"><X className="h-4 w-4 mr-1" />Fermer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ doc, userId, onClose, onDone }) {
  const [validUntil, setValidUntil] = useState(doc.valid_until || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Sélectionnez un fichier"); return; }
    if (!validUntil) { toast.error("Renseignez la date de validité"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("user_id", userId);
      fd.append("doc_type", doc.doc_type);
      fd.append("valid_until", validUntil);
      fd.append("file", file);
      await api.post("/tech-docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${doc.label} mis à jour`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Déposer — {doc.label}</DialogTitle>
          <DialogDescription>Fichier PDF/JPG/PNG (max 10 Mo) et date de fin de validité obligatoires.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Date de fin de validité</Label>
            <Input data-testid="techdoc-validity-input" type="date" required value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </div>
          <div>
            <Label>Fichier (PDF, JPG, PNG — max 10 Mo)</Label>
            <Input data-testid="techdoc-file-input" type="file" accept="application/pdf,image/png,image/jpeg" required onChange={(e) => setFile(e.target.files?.[0])} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button data-testid="techdoc-submit" type="submit" disabled={busy} className="bg-[#0066FF] hover:bg-[#0052CC]">{busy ? "…" : "Enregistrer"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
